// src/pages/DiemDanh/DiemDanh.js
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthContext } from "../../context/AuthContext";
import { useDiemDanh } from "../../hooks";
import { getLichSuDiemDanhHS } from "../../services/hocSinhService";
import { getDiemDanhByNgay } from "../../services/diemDanhService";
import { formatDate } from "../../utils";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../../config/firebase";
import { useToast } from "../../components/Toast/ToastContext";
import useUnsavedChanges from "../../hooks/useUnsavedChanges";
import UnsavedModal from "../../components/UnsavedModal/UnsavedModal";
import BottomSheet from "../../components/BottomSheet/BottomSheet";
import { getNamHocActive, getActiveHocKy } from "../../services/namHocService";
import DiemDanhMobile from "./DiemDanhMobile";
import DiemDanhDesktop from "./DiemDanhDesktop";
import DiemDanhHistory from "./DiemDanhHistory";

// ===== CACHE =====
const CACHE_KEY = 'diemdanh_cache';
const CACHE_DURATION = 30 * 60 * 1000;

const loadCache = () => { try { return JSON.parse(localStorage.getItem(CACHE_KEY)) || {}; } catch (e) { return {}; } };
const saveCache = (cache) => { try { localStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch (e) {} };

export const STATUS_STYLE = {
  coMat: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-500', icon: 'check_circle', label: 'Có mặt', dot: 'bg-green-500' },
  vangPhep: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-500', icon: 'schedule', label: 'Vắng phép', dot: 'bg-yellow-500' },
  vangKhongPhep: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-500', icon: 'cancel', label: 'Vắng KP', dot: 'bg-red-500' },
  diTre: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-500', icon: 'timer', label: 'Đi trễ', dot: 'bg-gray-500' },
  null: { bg: 'bg-gray-50', text: 'text-gray-400', border: 'border-gray-200', icon: 'help', label: 'Chưa ĐD', dot: 'bg-gray-300' },
};

const DiemDanh = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const lopId = user?.maLopCN || "";

  const searchParams = new URLSearchParams(location.search);
  const searchFromProfile = searchParams.get('search') || '';

  const [activeFilter, setActiveFilter] = useState(location.state?.filter || null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(searchFromProfile);
  const [showHistory, setShowHistory] = useState(false);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [selectedHS, setSelectedHS] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyMap, setHistoryMap] = useState({});
  const [hsFullStats, setHsFullStats] = useState({});
  const [tongBuoiDaDD, setTongBuoiDaDD] = useState(0);
  const [page, setPage] = useState(1);
  const [hocKy, setHocKy] = useState('HK1');
  const [namHoc, setNamHoc] = useState('2025-2026');
  const PER_PAGE = 10;
  const searchRef = useRef(null);
  const { success, error } = useToast();
  const cacheRef = useRef(loadCache());

  const {
    hocSinh, diemDanh, ghiChu, ngayChon, loading, saving, saved,
    setTrangThai, setGhiChuHS, setNgayChon, danhDauTatCa, save, changeDay, veHomNay,
    coThayDoi, soChuaDiemDanh, counts, TRANG_THAI
  } = useDiemDanh(lopId, user.uid);

  useEffect(() => {
    const init = async () => {
      try { const nh = await getNamHocActive(); if (nh) setNamHoc(nh.tenNamHoc || nh.id || '2025-2026'); } catch(e) {}
      try { const hk = await getActiveHocKy(); if (hk) setHocKy(hk.hocKy); } catch(e) {}
    };
    init();
  }, []);

  const [hasChanges, setHasChanges] = useState(false);
  const { showConfirm, confirmLeave, confirmStay } = useUnsavedChanges(hasChanges);
  useEffect(() => { setHasChanges(coThayDoi); }, [coThayDoi, diemDanh, ghiChu]);

  const handleSaveAndLeave = async () => {
    const r = await save();
    if (r.success) { success(r.message); fetchHistory(); confirmLeave(); } else { error(r.message); }
  };

  const fetchHistory = useCallback(async () => {
    if (!ngayChon || hocSinh.length === 0) return;
    const cacheKey = `history_${lopId}_${ngayChon}`;
    const cached = cacheRef.current[cacheKey];
    if (cached && cached.timestamp > Date.now() - CACHE_DURATION) { setHistoryMap(cached.data); return; }
    const sundays = [];
    const current = new Date(ngayChon);
    for (let i = 0; i < 5; i++) { const d = new Date(current); d.setDate(d.getDate() - (i * 7)); sundays.push(d.toISOString().split('T')[0]); }
    sundays.reverse();
    const map = {}, ddByDate = {};
    for (const sunday of sundays) { if (!ddByDate[sunday]) ddByDate[sunday] = await getDiemDanhByNgay(lopId, sunday); }
    for (const hs of hocSinh) { map[hs.id] = sundays.map(sunday => { const dd = ddByDate[sunday] || []; const hsDD = dd.find(d => d.hocSinhId === hs.id); return hsDD ? hsDD.trangThai : null; }); }
    cacheRef.current[cacheKey] = { data: map, timestamp: Date.now() }; saveCache(cacheRef.current); setHistoryMap(map);
  }, [ngayChon, hocSinh, lopId]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  useEffect(() => {
    const fetchFullStats = async () => {
      if (!lopId || hocSinh.length === 0) return;
      const cacheKey = `stats_${lopId}_${namHoc}_${hocKy}`;
      const cached = cacheRef.current[cacheKey];
      if (cached && cached.timestamp > Date.now() - CACHE_DURATION && !saved) { setTongBuoiDaDD(cached.tongBuoi); setHsFullStats(cached.stats); return; }
      try {
        const snap = await getDocs(query(collection(db, "diemDanh"), where("lopId", "==", lopId)));
        const allDD = snap.docs.map(d => d.data()).filter(d => {
          if (!d.ngayHoc) return false;
          const date = new Date(d.ngayHoc); const month = date.getMonth() + 1; const year = date.getFullYear();
          const [namBD] = namHoc.split('-'); const namKT = namBD ? parseInt(namBD) + 1 : 2026;
          if (!((year === parseInt(namBD) && month >= 8) || (year === namKT && month <= 5))) return false;
          return hocKy === 'HK1' ? (month >= 8 && month <= 12) : (month >= 1 && month <= 5);
        });
        const cacNgayDD = [...new Set(allDD.map(d => d.ngayHoc))]; setTongBuoiDaDD(cacNgayDD.length);
        const stats = {};
        for (const hs of hocSinh) { const hsDD = allDD.filter(d => d.hocSinhId === hs.id); stats[hs.id] = { coMat: hsDD.filter(d => d.trangThai === 'coMat' || d.trangThai === 'diTre').length, vangPhep: hsDD.filter(d => d.trangThai === 'vangPhep').length, vangKP: hsDD.filter(d => d.trangThai === 'vangKhongPhep').length }; }
        setHsFullStats(stats);
        cacheRef.current[cacheKey] = { tongBuoi: cacNgayDD.length, stats, timestamp: Date.now() }; saveCache(cacheRef.current);
      } catch (err) { console.error("Lỗi thống kê:", err); }
    };
    fetchFullStats();
  }, [lopId, hocSinh, saved, hocKy, namHoc]);

  const filtered = useMemo(() => {
    let r = hocSinh;
    if (activeFilter === 'coMat') r = r.filter(hs => diemDanh[hs.id] === 'coMat' || diemDanh[hs.id] === 'diTre');
    else if (activeFilter === 'vangPhep') r = r.filter(hs => diemDanh[hs.id] === 'vangPhep');
    else if (activeFilter === 'vangKP') r = r.filter(hs => diemDanh[hs.id] === 'vangKhongPhep');
    else if (activeFilter === 'chuaDD') r = r.filter(hs => diemDanh[hs.id] === null);
    if (searchTerm.trim()) { const t = searchTerm.toLowerCase(); r = r.filter(hs => (hs.hoTen || '').toLowerCase().includes(t) || (hs.tenThanh || '').toLowerCase().includes(t)); }
    return r;
  }, [hocSinh, activeFilter, diemDanh, searchTerm]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = useMemo(() => filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE), [filtered, page]);
  useEffect(() => { setPage(1); }, [activeFilter, searchTerm]);

  const openHistory = useCallback(async (hs) => {
    setSelectedHS(hs); setLoadingHistory(true); setShowHistory(true);
    try { const data = await getLichSuDiemDanhHS(hs.id, lopId); setHistoryData((data || []).sort((a, b) => b.ngayHoc.localeCompare(a.ngayHoc))); } catch (err) { setHistoryData([]); }
    finally { setLoadingHistory(false); }
  }, [lopId]);

  const hasAutoOpened = useRef(false);
  useEffect(() => {
    if (searchFromProfile && filtered.length === 1 && !loading && hocSinh.length > 0 && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      setSelectedHS(filtered[0]);
      setShowFullHistory(true);
    }
  }, [filtered, searchFromProfile, loading, hocSinh.length]);

  const handleSave = async () => {
    const r = await save();
    if (r.success) { 
      success(r.message); 
      window.dispatchEvent(new Event('refreshBadge'));
      const cacheKey = `history_${lopId}_${ngayChon}`;
      delete cacheRef.current[cacheKey];
      saveCache(cacheRef.current);
      fetchHistory();
    } else { error(r.message); }
  };

  const siSo = hocSinh.length;
  const coMat = (counts['coMat'] || 0) + (counts['diTre'] || 0);
  const vangPhep = counts['vangPhep'] || 0;
  const vangKP = counts['vangKhongPhep'] || 0;

  if (loading) return <div className="flex items-center justify-center h-64"><span className="material-symbols-outlined text-primary animate-spin text-4xl">sync</span></div>;

  return (
    <div className="space-y-3 md:space-y-4 pb-20 md:pb-6">
      <UnsavedModal show={showConfirm} onStay={confirmStay} onLeave={confirmLeave} onSaveAndLeave={handleSaveAndLeave} />

      {showFullHistory && selectedHS ? (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-[24px] md:text-[28px] font-bold text-primary">
              📋 Điểm danh: {selectedHS.tenThanh ? `${selectedHS.tenThanh} - ` : ''}{selectedHS.hoTen}
            </h2>
            <button onClick={() => setShowFullHistory(false)} className="p-2 rounded-full hover:bg-surface-container transition-colors">
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>
          <DiemDanhHistory selectedHS={selectedHS} fullScreen />
        </>
      ) : (
        <>
          {/* DESKTOP TOOLBAR */}
          <div className="hidden md:flex items-center gap-2">
            <div className="flex items-center gap-1 bg-surface rounded-xl px-2 py-2 border border-outline-variant shadow-sm">
              <button onClick={() => changeDay(-1)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-surface-container"><span className="material-symbols-outlined text-sm">chevron_left</span></button>
              <input type="date" value={ngayChon} onChange={e => setNgayChon(e.target.value)} className="bg-transparent border-none outline-none text-body-md text-primary font-semibold cursor-pointer w-[115px] text-center" />
              <button onClick={() => changeDay(1)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-surface-container"><span className="material-symbols-outlined text-sm">chevron_right</span></button>
              <button onClick={veHomNay} className="px-2 py-0.5 rounded-full bg-primary-container text-on-primary-container text-[10px] font-bold whitespace-nowrap">HN</button>
            </div>
            <div className="flex items-center gap-1.5 bg-secondary-container rounded-xl px-3 py-2 text-caption font-semibold text-on-secondary-container whitespace-nowrap"><span className="material-symbols-outlined text-[16px]">school</span>{namHoc} • {hocKy}</div>
            <select value={hocKy} onChange={e => setHocKy(e.target.value)} className="bg-surface border border-outline-variant rounded-xl px-3 py-2 text-body-md font-semibold text-primary outline-none cursor-pointer"><option value="HK1">HK1</option><option value="HK2">HK2</option></select>
            <div className="relative flex-1 min-w-0">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline">search</span>
              <input type="text" placeholder="Tìm học sinh..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-surface border border-outline-variant rounded-xl text-body-md focus:ring-2 focus:ring-secondary outline-none" />
              {searchTerm && <button onClick={() => setSearchTerm("")} className="absolute right-3 top-1/2 -translate-y-1/2"><span className="material-symbols-outlined text-[18px] text-outline">close</span></button>}
            </div>
            <button onClick={() => danhDauTatCa('coMat')} className="flex-shrink-0 w-10 h-10 bg-green-100 text-green-700 rounded-xl flex items-center justify-center active:scale-95" title="Tất cả có mặt"><span className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span></button>
            <button onClick={handleSave} disabled={saving} className={`flex-shrink-0 px-4 py-2.5 rounded-full font-body-lg flex items-center gap-1.5 shadow-md active:scale-95 transition-all ${saved ? 'bg-green-600 text-white' : 'bg-primary text-on-primary hover:bg-primary/90'}`}><span className="material-symbols-outlined text-[18px]">{saving ? 'sync' : saved ? 'check_circle' : 'save'}</span><span className="hidden sm:inline">{saving ? 'Đang lưu...' : saved ? 'Đã lưu!' : 'Lưu'}</span></button>
          </div>

          {/* MOBILE TOOLBAR */}
          <div className="md:hidden space-y-2">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1 bg-surface rounded-xl px-2 py-2 border border-outline-variant shadow-sm">
                <button onClick={() => changeDay(-1)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-surface-container"><span className="material-symbols-outlined text-sm">chevron_left</span></button>
                <input type="date" value={ngayChon} onChange={e => setNgayChon(e.target.value)} className="bg-transparent border-none outline-none text-body-md text-primary font-semibold cursor-pointer w-[115px] text-center" />
                <button onClick={() => changeDay(1)} className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-surface-container"><span className="material-symbols-outlined text-sm">chevron_right</span></button>
                <button onClick={veHomNay} className="px-2 py-0.5 rounded-full bg-primary-container text-on-primary-container text-[10px] font-bold whitespace-nowrap">HN</button>
              </div>
              <select value={hocKy} onChange={e => setHocKy(e.target.value)} className="bg-surface border border-outline-variant rounded-lg px-2 py-2 text-caption font-semibold text-primary outline-none"><option value="HK1">HK1</option><option value="HK2">HK2</option></select>
              <div className="flex-1" />
              <button onClick={handleSave} disabled={saving} className={`flex-shrink-0 px-4 py-2.5 rounded-full font-body-lg flex items-center gap-1.5 shadow-md active:scale-95 transition-all ${saved ? 'bg-green-600 text-white' : 'bg-primary text-on-primary hover:bg-primary/90'}`}><span className="material-symbols-outlined text-[18px]">{saving ? 'sync' : saved ? 'check_circle' : 'save'}</span></button>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => { setSearchOpen(true); setTimeout(() => searchRef.current?.focus(), 100); }} className="flex-1 flex items-center gap-2 px-4 py-2.5 bg-surface border border-outline-variant rounded-xl text-body-md text-on-surface-variant"><span className="material-symbols-outlined text-outline">search</span><span className="text-on-surface-variant/50">Tìm học sinh...</span></button>
              <button onClick={() => danhDauTatCa('coMat')} className="flex-shrink-0 px-4 py-2.5 bg-green-100 text-green-700 rounded-xl text-body-md font-bold flex items-center gap-1.5 active:scale-95"><span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>check_circle</span>Tất cả CM</button>
            </div>
          </div>

          {/* FILTER BUTTONS */}
          <div className="flex gap-1.5">
            <button onClick={() => setActiveFilter(activeFilter === 'all' ? null : 'all')} className={`flex-1 flex flex-col items-center py-2 rounded-xl transition-all text-[10px] ${!activeFilter || activeFilter === 'all' ? 'bg-primary-container text-on-primary-container font-bold' : 'bg-surface text-on-surface-variant border border-outline-variant'}`}><span className="text-lg font-bold">{siSo}</span>Tất cả</button>
            <button onClick={() => setActiveFilter(activeFilter === 'coMat' ? null : 'coMat')} className={`flex-1 flex flex-col items-center py-2 rounded-xl transition-all text-[10px] ${activeFilter === 'coMat' ? 'bg-green-100 text-green-700 font-bold' : 'bg-surface text-on-surface-variant border border-outline-variant'}`}><span className="text-lg font-bold">{coMat}</span>Có mặt</button>
            <button onClick={() => setActiveFilter(activeFilter === 'vangPhep' ? null : 'vangPhep')} className={`flex-1 flex flex-col items-center py-2 rounded-xl transition-all text-[10px] ${activeFilter === 'vangPhep' ? 'bg-yellow-100 text-yellow-700 font-bold' : 'bg-surface text-on-surface-variant border border-outline-variant'}`}><span className="text-lg font-bold">{vangPhep}</span>Có phép</button>
            <button onClick={() => setActiveFilter(activeFilter === 'vangKP' ? null : 'vangKP')} className={`flex-1 flex flex-col items-center py-2 rounded-xl transition-all text-[10px] ${activeFilter === 'vangKP' ? 'bg-red-100 text-red-700 font-bold' : 'bg-surface text-on-surface-variant border border-outline-variant'}`}><span className="text-lg font-bold">{vangKP}</span>K.P</button>
            <button onClick={() => setActiveFilter(activeFilter === 'chuaDD' ? null : 'chuaDD')} className={`flex-1 flex flex-col items-center py-2 rounded-xl transition-all text-[10px] ${activeFilter === 'chuaDD' ? 'bg-gray-200 text-gray-600 font-bold' : 'bg-surface text-on-surface-variant border border-outline-variant'}`}><span className="text-lg font-bold">{soChuaDiemDanh}</span>Chưa ĐD</button>
          </div>

          <div className="md:hidden">
            <DiemDanhMobile filtered={filtered} diemDanh={diemDanh} ghiChu={ghiChu} historyMap={historyMap} hsFullStats={hsFullStats} tongBuoiDaDD={tongBuoiDaDD} searchOpen={searchOpen} searchTerm={searchTerm} searchRef={searchRef} setSearchOpen={setSearchOpen} setSearchTerm={setSearchTerm} setTrangThai={setTrangThai} setGhiChuHS={setGhiChuHS} openHistory={openHistory} STATUS_STYLE={STATUS_STYLE} />
          </div>

          <div className="hidden md:block">
            <DiemDanhDesktop paginated={paginated} filtered={filtered} diemDanh={diemDanh} ghiChu={ghiChu} historyMap={historyMap} hsFullStats={hsFullStats} tongBuoiDaDD={tongBuoiDaDD} page={page} totalPages={totalPages} PER_PAGE={PER_PAGE} namHoc={namHoc} hocKy={hocKy} coThayDoi={coThayDoi} saved={saved} setPage={setPage} setTrangThai={setTrangThai} setGhiChuHS={setGhiChuHS} openHistory={openHistory} TRANG_THAI={TRANG_THAI} STATUS_STYLE={STATUS_STYLE} />
          </div>

          <BottomSheet isOpen={showHistory} onClose={() => setShowHistory(false)} title={selectedHS ? `${selectedHS.tenThanh || ''} ${selectedHS.hoTen}` : 'Lịch sử'}>
            {selectedHS && <button onClick={() => { setShowHistory(false); navigate(`/hoc-sinh/${selectedHS.id}`); }} className="w-full py-2.5 mb-3 bg-primary text-on-primary rounded-xl text-body-md font-semibold">👤 Xem hồ sơ đầy đủ →</button>}
            {loadingHistory ? <div className="text-center py-4">Đang tải...</div> : historyData.length === 0 ? <div className="text-center py-4 text-on-surface-variant">Chưa có dữ liệu</div> : <div className="space-y-2 max-h-80 overflow-y-auto">{historyData.map((item, idx) => { const s = STATUS_STYLE[item.trangThai] || STATUS_STYLE['null']; return <div key={idx} className="flex items-center gap-3 p-3 bg-surface-container-low rounded-lg"><div className={`w-3 h-3 rounded-full ${s.dot}`} /><div className="flex-1"><p className="text-body-md font-semibold">{formatDate(item.ngayHoc)}</p><p className={`text-caption ${s.text}`}>{s.label}</p>{item.ghiChu && <p className="text-caption text-on-surface-variant mt-0.5">💬 {item.ghiChu}</p>}</div></div>; })}</div>}
          </BottomSheet>
        </>
      )}
    </div>
  );
};

export default DiemDanh;